/**
 * Documents Store — manages document repository, categories, and versions.
 * Domain: Document Management
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'

export const useDocumentsStore = defineStore('documents', () => {
  // ─── State ───────────────────────────────────────────────────────────────
  const items          = ref([])
  const categories     = ref([])
  const loading        = ref(false)
  const searchQuery    = ref('')
  const categoryFilter = ref('')
  const typeFilter     = ref('')

  // ─── Getters ─────────────────────────────────────────────────────────────
  const filtered = computed(() => {
    let list = items.value
    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase()
      list = list.filter(d =>
        d.title?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q)
      )
    }
    if (categoryFilter.value) list = list.filter(d => d.category?.id === categoryFilter.value)
    if (typeFilter.value)     list = list.filter(d => d.document_type === typeFilter.value)
    return list
  })

  // ─── Actions ─────────────────────────────────────────────────────────────
  const fetchAll = async (params = {}) => {
    loading.value = true
    try {
      const response = await api.get('/documents', { params })
      items.value = response.data.documents || []
      return items.value
    } catch (error) {
      console.error('Failed to fetch documents:', error)
      return []
    } finally {
      loading.value = false
    }
  }

  const fetchById = async (id) => {
    try {
      const response = await api.get(`/documents/${id}`)
      return response.data
    } catch (error) {
      console.error(`Failed to fetch document ${id}:`, error)
      throw error
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await api.get('/documents/categories')
      categories.value = response.data.categories || []
      return categories.value
    } catch (error) {
      console.error('Failed to fetch document categories:', error)
      return []
    }
  }

  const upload = async (formData) => {
    try {
      const response = await api.post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      const newItem = response.data.document
      items.value.unshift(newItem)
      return { success: true, document: newItem }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Upload failed' }
    }
  }

  const update = async (id, data) => {
    try {
      const response = await api.put(`/documents/${id}`, data)
      const updated = response.data.document
      const idx = items.value.findIndex(d => d.id === id)
      if (idx !== -1) items.value[idx] = updated
      return { success: true, document: updated }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update document' }
    }
  }

  const remove = async (id) => {
    try {
      await api.delete(`/documents/${id}`)
      items.value = items.value.filter(d => d.id !== id)
      return { success: true }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to delete document' }
    }
  }

  const addComment = async (documentId, text) => {
    try {
      const response = await api.post(`/documents/${documentId}/comments`, { text })
      return { success: true, comment: response.data.comment }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to add comment' }
    }
  }

  const createCategory = async (data) => {
    try {
      const response = await api.post('/documents/categories', data)
      const newCat = response.data.category
      categories.value.push(newCat)
      return { success: true, category: newCat }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to create category' }
    }
  }

  const removeCategory = async (id) => {
    try {
      await api.delete(`/documents/categories/${id}`)
      categories.value = categories.value.filter(c => c.id !== id)
      return { success: true }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to delete category' }
    }
  }

  return {
    // State
    items, categories, loading, searchQuery, categoryFilter, typeFilter,
    // Getters
    filtered,
    // Actions
    fetchAll, fetchById, fetchCategories, upload, update, remove,
    addComment, createCategory, removeCategory
  }
})
